/**
 * Phase CANCEL-DRAFT (Part B / FE) — tiered "Cancel this application?"
 * warning modal shared by the customer + admin surfaces.
 *
 * Props:
 *   open, onOpenChange, target: { entityType: "submission"|"invite", ... },
 *   surface: "customer" | "admin",
 *   onCancelled: async (result) => void,
 *   fieldsFilledCount: number,   // used to pick Tier 1 vs Tier 2
 *   filesCount: number,
 *   companyInfoPresent, guarantorInfoPresent  // for chip rendering
 *
 * Behaviour:
 *  - Tier 1 (no data entered / un-opened invite) → plain confirm copy.
 *  - Tier 2 (some data entered) → chip list of what's been saved.
 *  - Reason dropdown REQUIRED before the destructive button enables.
 *  - "Other" reason forces the free-text textarea to be non-empty.
 *  - Idempotent server behaviour — surfaced as a friendly info toast
 *    when the row was already cancelled.
 *
 * The modal fetches ``/submissions/cancel-reasons`` on first mount and
 * caches the payload in module scope so subsequent opens don't re-fetch.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

// Module-level cache — the metadata is static per deployment, so one
// fetch across every open of the modal is fine.
let _reasonsCache = null;

async function _loadReasons() {
    if (_reasonsCache) return _reasonsCache;
    const { data } = await api.get("/submissions/cancel-reasons");
    _reasonsCache = data;
    return data;
}

export default function CancelSubmissionModal({
    open,
    onOpenChange,
    target,
    surface = "customer",
    onCancelled,
    fieldsFilledCount = 0,
    filesCount = 0,
    companyInfoPresent = false,
    guarantorInfoPresent = false,
    // Optional override — the caller passes ``authless`` when this modal
    // is fired from the public magic-link footer, so we POST to the
    // token-based endpoint instead of the authed customer endpoint.
    invitePublicToken = null,
}) {
    const [reasons, setReasons] = useState(null);
    const [reasonCode, setReasonCode] = useState("");
    const [reasonText, setReasonText] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const rootRef = useRef(null);

    // Reset local state every time the modal closes/opens so a prior
    // reason selection doesn't leak across sessions.
    useEffect(() => {
        if (open) {
            setReasonCode("");
            setReasonText("");
            setError("");
            _loadReasons()
                .then((data) => setReasons(data))
                .catch(() => setReasons(null));
        }
    }, [open]);

    const list = useMemo(() => {
        if (!reasons) return [];
        return surface === "admin" ? reasons.admin : reasons.customer;
    }, [reasons, surface]);

    const maxFreeText = reasons?.max_free_text || 500;
    const isOther = reasonCode === "other";
    const trimmedText = (reasonText || "").trim();
    const submitDisabled = (
        busy ||
        !reasonCode ||
        (isOther && !trimmedText) ||
        trimmedText.length > maxFreeText
    );

    // Tier decision — Tier 1 whenever there's no data on the row.
    const isTier1 = fieldsFilledCount === 0 && filesCount === 0;

    const doCancel = useCallback(async () => {
        if (submitDisabled) return;
        setBusy(true);
        setError("");
        try {
            const body = { reason_code: reasonCode, reason_text: trimmedText || null };
            let url;
            if (target?.entityType === "invite") {
                if (invitePublicToken) {
                    url = `/customer/invites/${encodeURIComponent(invitePublicToken)}/cancel`;
                } else if (surface === "admin") {
                    url = `/admin/invites/${target.id}/cancel`;
                } else {
                    throw new Error("Missing invitePublicToken for customer invite cancel");
                }
            } else {
                url = surface === "admin"
                    ? `/admin/submissions/${target.id}/cancel-draft`
                    : `/customer/submissions/${target.id}/cancel`;
            }
            const { data } = await api.post(url, body);
            if (data?.already_cancelled) {
                toast.info("This application was already cancelled.");
            } else {
                toast.success("Application cancelled.");
            }
            onOpenChange?.(false);
            await onCancelled?.(data);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }, [submitDisabled, reasonCode, trimmedText, target, surface, invitePublicToken, onOpenChange, onCancelled]);

    return (
        <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange?.(o)}>
            <DialogContent
                ref={rootRef}
                data-testid="cancel-submission-modal"
                data-tier={isTier1 ? "1" : "2"}
                className="max-w-lg"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle size={18} className="text-amber-600" />
                        Cancel this application?
                    </DialogTitle>
                    <DialogDescription>
                        {isTier1
                            ? "This invite hasn't been started. Cancelling will invalidate the link — you can request a new invite anytime."
                            : "You're about to cancel an application in progress. Here's what happens:"}
                    </DialogDescription>
                </DialogHeader>

                {!isTier1 && (
                    <div className="space-y-3 text-sm text-slate-700">
                        <ul className="list-disc pl-5 space-y-1 text-slate-600">
                            <li>Your saved data and any uploaded documents will be hidden from your dashboard.</li>
                            <li>The current invite link will stop working.</li>
                            <li>You can start a new application anytime — we&apos;ll prefill what we can from your last attempt.</li>
                        </ul>
                        <div
                            className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 flex flex-wrap gap-x-3 gap-y-1"
                            data-testid="cancel-tier2-chips"
                        >
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mr-2">
                                Already entered
                            </span>
                            {companyInfoPresent && (
                                <span className="inline-flex items-center gap-1 text-xs text-slate-700" data-testid="cancel-chip-company">
                                    <CheckCircle2 size={11} className="text-emerald-600" /> Company info
                                </span>
                            )}
                            {filesCount > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs text-slate-700" data-testid="cancel-chip-files">
                                    <CheckCircle2 size={11} className="text-emerald-600" /> {filesCount} document{filesCount === 1 ? "" : "s"} uploaded
                                </span>
                            )}
                            {guarantorInfoPresent && (
                                <span className="inline-flex items-center gap-1 text-xs text-slate-700" data-testid="cancel-chip-guarantor">
                                    <CheckCircle2 size={11} className="text-emerald-600" /> Guarantor info
                                </span>
                            )}
                            {!companyInfoPresent && filesCount === 0 && !guarantorInfoPresent && fieldsFilledCount > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs text-slate-700" data-testid="cancel-chip-generic">
                                    <CheckCircle2 size={11} className="text-emerald-600" /> {fieldsFilledCount} field{fieldsFilledCount === 1 ? "" : "s"} filled
                                </span>
                            )}
                        </div>
                    </div>
                )}

                <div className="mt-2 space-y-2">
                    <Label htmlFor="cancel-reason-code" className="text-sm font-medium">
                        Reason <span className="text-red-600">*</span>
                    </Label>
                    <select
                        id="cancel-reason-code"
                        data-testid="cancel-reason-select"
                        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-700"
                        value={reasonCode}
                        onChange={(e) => setReasonCode(e.target.value)}
                        disabled={busy || !reasons}
                    >
                        <option value="">Select a reason…</option>
                        {list.map((r) => (
                            <option key={r.code} value={r.code}>{r.label}</option>
                        ))}
                    </select>
                    {isOther && (
                        <div>
                            <Label htmlFor="cancel-reason-text" className="text-xs text-slate-500">
                                Please tell us more <span className="text-red-600">*</span>
                            </Label>
                            <Textarea
                                id="cancel-reason-text"
                                data-testid="cancel-reason-text"
                                value={reasonText}
                                onChange={(e) => setReasonText(e.target.value)}
                                maxLength={maxFreeText}
                                placeholder="A short note helps us follow up if needed."
                                disabled={busy}
                                className="mt-1"
                                rows={3}
                            />
                            <p className="text-[11px] text-slate-400 mt-0.5">
                                {trimmedText.length}/{maxFreeText} characters
                            </p>
                        </div>
                    )}
                </div>

                {error && (
                    <div
                        role="alert"
                        data-testid="cancel-error"
                        className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                    >
                        {error}
                    </div>
                )}

                <DialogFooter className="gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange?.(false)}
                        disabled={busy}
                        data-testid="cancel-modal-keep-open"
                    >
                        Keep open
                    </Button>
                    <Button
                        type="button"
                        onClick={doCancel}
                        disabled={submitDisabled}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid="cancel-modal-confirm"
                    >
                        {busy ? "Cancelling…" : "Cancel application"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
